import { useMemo } from 'react';
import { Extension, StateEffect, StateField } from '@codemirror/state';
import { EditorView, keymap } from '@codemirror/view';

type QuickFix = {
  title: string;
  kind: 'import' | 'refactor' | 'fix' | 'extract';
  apply: (view: EditorView) => void;
};

type QuickFixState = {
  fixes: QuickFix[];
  position: { line: number; from: number; to: number };
  visible: boolean;
};

// Common React imports
const COMMON_IMPORTS: Record<string, { module: string; isDefault?: boolean }> = {
  useState: { module: 'react' },
  useEffect: { module: 'react' },
  useCallback: { module: 'react' },
  useMemo: { module: 'react' },
  useRef: { module: 'react' },
  useContext: { module: 'react' },
  useReducer: { module: 'react' },
  createContext: { module: 'react' },
  forwardRef: { module: 'react' },
  memo: { module: 'react' },
  lazy: { module: 'react' },
  Suspense: { module: 'react' },
  Fragment: { module: 'react' },
  Component: { module: 'react' },
  PureComponent: { module: 'react' },
  React: { module: 'react', isDefault: true },
  ReactDOM: { module: 'react-dom', isDefault: true },
  Link: { module: 'react-router-dom' },
  useNavigate: { module: 'react-router-dom' },
  useParams: { module: 'react-router-dom' },
  useLocation: { module: 'react-router-dom' },
  Routes: { module: 'react-router-dom' },
  Route: { module: 'react-router-dom' },
  BrowserRouter: { module: 'react-router-dom' },
  clsx: { module: 'clsx', isDefault: true },
  cn: { module: 'clsx', isDefault: true },
  styled: { module: 'styled-components', isDefault: true },
  css: { module: 'styled-components' },
  axios: { module: 'axios', isDefault: true },
  motion: { module: 'framer-motion' },
  AnimatePresence: { module: 'framer-motion' },
};

// State effects
const setQuickFixes = StateEffect.define<QuickFixState>();
const hideQuickFixes = StateEffect.define<null>();

// State field
const quickFixState = StateField.define<QuickFixState>({
  create: () => ({ fixes: [], position: { line: 0, from: 0, to: 0 }, visible: false }),
  update(value, tr) {
    for (const effect of tr.effects) {
      if (effect.is(setQuickFixes)) {
        return effect.value;
      }
      if (effect.is(hideQuickFixes)) {
        return { ...value, visible: false };
      }
    }
    // Hide on doc change or selection change
    if (tr.docChanged) {
      return { ...value, visible: false };
    }
    return value;
  },
});

// Find undefined identifiers that could be imported
function findMissingImports(code: string, pos: number): { name: string; line: number }[] {
  const missing: { name: string; line: number }[] = [];
  const lines = code.split('\n');

  // Get existing imports
  const existingImports = new Set<string>();
  const importRegex = /import\s+(?:(\w+)|.*?\{([^}]+)\})\s+from/g;
  let match;
  while ((match = importRegex.exec(code)) !== null) {
    if (match[1]) existingImports.add(match[1]);
    if (match[2]) {
      match[2].split(',').forEach((name) => {
        const trimmed = name.trim().split(' as ')[0].replace('type ', '').trim();
        if (trimmed) existingImports.add(trimmed);
      });
    }
  }

  // Find the line at position
  let currentPos = 0;
  for (let i = 0; i < lines.length; i++) {
    const lineEnd = currentPos + lines[i].length + 1;
    if (pos >= currentPos && pos < lineEnd) {
      // Check for undefined identifiers on this line
      const line = lines[i];
      const identifierRegex = /\b([A-Z][a-zA-Z0-9]*|use[A-Z][a-zA-Z0-9]*)\b/g;
      let idMatch;
      while ((idMatch = identifierRegex.exec(line)) !== null) {
        const name = idMatch[1];
        if (COMMON_IMPORTS[name] && !existingImports.has(name)) {
          missing.push({ name, line: i });
        }
      }
      break;
    }
    currentPos = lineEnd;
  }

  return missing;
}

// Generate import statement
function generateImport(name: string): string {
  const info = COMMON_IMPORTS[name];
  if (!info) return '';

  if (info.isDefault) {
    return `import ${name} from '${info.module}';\n`;
  }
  return `import { ${name} } from '${info.module}';\n`;
}

// Find the position to insert imports
function findImportInsertPosition(code: string): number {
  const lines = code.split('\n');
  let lastImportEnd = 0;
  let pos = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.trim().startsWith('import ')) {
      // Find end of this import (handle multiline)
      let j = i;
      while (j < lines.length && !lines[j].includes(';') && !lines[j].includes("'")) {
        j++;
      }
      pos = 0;
      for (let k = 0; k <= j; k++) {
        pos += lines[k].length + 1;
      }
      lastImportEnd = pos;
    } else if (line.trim() && !line.trim().startsWith('//') && !line.trim().startsWith('/*')) {
      // Found non-import, non-comment line
      break;
    }
    pos += line.length + 1;
  }

  return lastImportEnd;
}

// Check if cursor is on a function that could be converted to arrow
function canConvertToArrow(code: string, pos: number): { from: number; to: number; name: string } | null {
  const lines = code.split('\n');
  let currentPos = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lineEnd = currentPos + line.length + 1;

    if (pos >= currentPos && pos < lineEnd) {
      // Check if this line has a traditional function
      const funcMatch = line.match(/^(\s*)function\s+(\w+)\s*\(([^)]*)\)\s*\{?/);
      if (funcMatch) {
        return {
          from: currentPos,
          to: lineEnd,
          name: funcMatch[2],
        };
      }
    }
    currentPos = lineEnd;
  }

  return null;
}

// Convert function to arrow function
function convertToArrow(view: EditorView, from: number, to: number, name: string): void {
  const code = view.state.doc.toString();
  const funcCode = code.slice(from, to);

  // Simple conversion: function name(params) { -> const name = (params) => {
  const converted = funcCode.replace(
    /^(\s*)function\s+(\w+)\s*\(([^)]*)\)\s*\{?/,
    '$1const $2 = ($3) => {'
  );

  view.dispatch({
    changes: { from, to, insert: converted },
  });
}

// Get quick fixes for current position
function getQuickFixes(view: EditorView): QuickFix[] {
  const fixes: QuickFix[] = [];
  const pos = view.state.selection.main.head;
  const code = view.state.doc.toString();

  // Check for missing imports
  const missing = findMissingImports(code, pos);
  for (const { name } of missing) {
    fixes.push({
      title: `Import '${name}' from '${COMMON_IMPORTS[name].module}'`,
      kind: 'import',
      apply: (v) => {
        const insertPos = findImportInsertPosition(v.state.doc.toString());
        const importStatement = generateImport(name);
        v.dispatch({
          changes: { from: insertPos, to: insertPos, insert: importStatement },
        });
      },
    });
  }

  // Check for function to arrow conversion
  const arrowConvert = canConvertToArrow(code, pos);
  if (arrowConvert) {
    fixes.push({
      title: `Convert '${arrowConvert.name}' to arrow function`,
      kind: 'refactor',
      apply: (v) => {
        convertToArrow(v, arrowConvert.from, arrowConvert.to, arrowConvert.name);
      },
    });
  }

  // Add extract function suggestion if there's a selection
  const selection = view.state.selection.main;
  if (!selection.empty && selection.to - selection.from > 10) {
    fixes.push({
      title: 'Extract to function',
      kind: 'extract',
      apply: (v) => {
        const selectedCode = v.state.sliceDoc(selection.from, selection.to);
        const funcName = 'extractedFunction';
        const newFunc = `\nfunction ${funcName}() {\n  ${selectedCode.split('\n').join('\n  ')}\n}\n`;

        // Insert function before current function/component
        const insertPos = findFunctionStart(v.state.doc.toString(), selection.from);

        v.dispatch({
          changes: [
            { from: insertPos, to: insertPos, insert: newFunc },
            { from: selection.from, to: selection.to, insert: `${funcName}()` },
          ],
        });
      },
    });
  }

  return fixes;
}

// Find the start of the current function for extraction
function findFunctionStart(code: string, pos: number): number {
  const before = code.slice(0, pos);
  const funcMatch = before.match(/(?:function\s+\w+|const\s+\w+\s*=\s*(?:async\s*)?\(|class\s+\w+)[^]*$/);
  if (funcMatch) {
    return pos - funcMatch[0].length;
  }
  return 0;
}

// Show quick fixes
function showQuickFixes(view: EditorView): boolean {
  const fixes = getQuickFixes(view);

  if (fixes.length === 0) {
    return false;
  }

  const pos = view.state.selection.main.head;
  const line = view.state.doc.lineAt(pos);

  view.dispatch({
    effects: setQuickFixes.of({
      fixes,
      position: { line: line.number, from: line.from, to: line.to },
      visible: true,
    }),
  });

  return true;
}

export function useQuickFix() {
  const quickFixExtension = useMemo<Extension>(() => {
    return [
      quickFixState,
      keymap.of([
        {
          key: 'Mod-.',
          run: showQuickFixes,
        },
        {
          key: 'Escape',
          run: (view) => {
            const state = view.state.field(quickFixState);
            if (state.visible) {
              view.dispatch({ effects: hideQuickFixes.of(null) });
              return true;
            }
            return false;
          },
        },
      ]),
      // Custom panel for quick fixes
      EditorView.updateListener.of((update) => {
        const state = update.state.field(quickFixState);

        // Find or create the quick fix container
        let container = update.view.dom.querySelector('.quick-fix-container') as HTMLElement;

        if (state.visible && state.fixes.length > 0) {
          if (!container) {
            container = document.createElement('div');
            container.className = 'quick-fix-container';
            update.view.dom.appendChild(container);
          }

          // Position at cursor
          const coords = update.view.coordsAtPos(update.state.selection.main.head);
          if (coords) {
            container.style.position = 'absolute';
            container.style.left = `${coords.left}px`;
            container.style.top = `${coords.bottom + 4}px`;
            container.style.display = 'block';

            // Render menu
            container.innerHTML = '';
            const menu = document.createElement('div');
            menu.className = 'quick-fix-menu';

            for (const fix of state.fixes) {
              const item = document.createElement('div');
              item.className = `quick-fix-item ${fix.kind}`;

              const icon = document.createElement('span');
              icon.className = 'quick-fix-icon';
              icon.textContent = fix.kind === 'import' ? '↓' : fix.kind === 'refactor' ? '⟳' : fix.kind === 'extract' ? '⤴' : '✓';

              const title = document.createElement('span');
              title.className = 'quick-fix-title';
              title.textContent = fix.title;

              item.appendChild(icon);
              item.appendChild(title);

              item.onclick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                fix.apply(update.view);
                update.view.dispatch({ effects: hideQuickFixes.of(null) });
              };

              menu.appendChild(item);
            }

            container.appendChild(menu);
          }
        } else if (container) {
          container.style.display = 'none';
        }
      }),
    ];
  }, []);

  return { quickFixExtension };
}
